<template>
  <v-card>
    <v-card-title>
      <span class="text-h5">{{ $t('user.editUser') }}</span>
    </v-card-title>
    <v-card-text>
      <v-row>
        <v-col cols="12" md="6">
          <v-form @submit.prevent="updatePassword" ref="passwordForm">
            <v-text-field
              v-model="newPassword"
              :label="$t('user.newPassword')"
              type="password"
              :rules="[rules.required, rules.min, rules.hasNumber, rules.hasUppercase]"
            ></v-text-field>
            <v-text-field
              v-model="confirmNewPassword"
              :label="$t('user.confirmNewPassword')"
              type="password"
              :rules="[rules.required, passwordMatch]"
            ></v-text-field>
            <v-btn color="primary" @click="updatePassword" :disabled="!isPasswordValid || passwordUpdating">
              {{ $t('user.updatePassword') }}
            </v-btn>
          </v-form>
        </v-col>
        <v-col cols="12" md="6">
          <div class="permissions-tree">
            <div v-for="genre in genreCategories" :key="genre.genre.inner_id" class="genre-item">
              <v-checkbox
                v-model="selectedPermissions"
                :label="genre.genre.name"
                :value="`g${genre.genre.inner_id}`"
                @change="updateCategorySelection(genre)"
              ></v-checkbox>
              <div v-for="category in genre.categories" :key="category.inner_id" class="category-item">
                <v-checkbox
                  v-model="selectedPermissions"
                  :label="category.name"
                  :value="`c${category.inner_id}`"
                  :disabled="!selectedPermissions.includes(`g${genre.genre.inner_id}`)"
                ></v-checkbox>
              </div>
            </div>
          </div>
          <v-btn color="primary" @click="updatePermissions" :disabled="!permissionsChanged || permissionsUpdating">
            {{ $t('user.updatePermissions') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="blue darken-1" text @click="$emit('close')">{{ $t('user.close') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue'
import axios from 'axios'
import yaml from 'js-yaml';

const props = defineProps({
  userId: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['close', 'user-updated'])

const newPassword = ref('')
const confirmNewPassword = ref('')
const passwordForm = ref(null)
const passwordUpdating = ref(false)
const permissionsUpdating = ref(false)

const genreCategories = ref([])
const selectedPermissions = ref([])
const originalPermissions = ref([])

const rules = {
  required: v => !!v || 'Field is required',
  min: v => v.length >= 8 || 'Password must be at least 8 characters',
  hasNumber: v => /\d/.test(v) || 'Password must contain a number',
  hasUppercase: v => /[A-Z]/.test(v) || 'Password must contain an uppercase letter'
}

const passwordMatch = computed(() => {
  return () => newPassword.value === confirmNewPassword.value || 'Passwords do not match'
})

const isPasswordValid = computed(() => {
  return newPassword.value && confirmNewPassword.value &&
      newPassword.value === confirmNewPassword.value &&
      newPassword.value.length >= 8 &&
      /\d/.test(newPassword.value) &&
      /[A-Z]/.test(newPassword.value)
})

const permissionsChanged = computed(() => {
  if (selectedPermissions.value.length !== originalPermissions.value.length) {
    return true;
  }
  const selectedSet = new Set(selectedPermissions.value);
  return originalPermissions.value.some(permission => !selectedSet.has(permission));
})

onMounted(async () => {
  await fetchGenreCategories()
  await fetchUserPermissions()
})

async function fetchGenreCategories() {
  try {
    const response = await axios.get('/api/genre-category-yaml', {
      responseType: 'text', // Specify the response type as text
    });
    const yamlData = response.data;
    const jsonData = yaml.load(yamlData); // Parse YAML to JavaScript object
    console.log('Genre categories data:', jsonData);
    genreCategories.value = jsonData;
  } catch (error) {
    console.error('Error fetching genre categories:', error);
  }
}

async function fetchUserPermissions() {
  try {
    const response = await axios.get(`/api/users/${props.userId}`)
    const user = response.data
    selectedPermissions.value = [
      ...user.allowed_genres.map(id => `g${id}`),
      ...user.allowed_categories.map(id => `c${id}`)
    ]
    originalPermissions.value = [...selectedPermissions.value]
  } catch (error) {
    console.error('Error fetching user permissions:', error)
  }
}

function updateCategorySelection(genre) {
  const genreId = `g${genre.genre.inner_id}`
  if (selectedPermissions.value.includes(genreId)) {
    // Add all categories of this genre
    genre.categories.forEach(category => {
      const categoryId = `c${category.inner_id}`
      if (!selectedPermissions.value.includes(categoryId)) {
        selectedPermissions.value.push(categoryId)
      }
    })
  } else {
    // Remove all categories of this genre
    selectedPermissions.value = selectedPermissions.value.filter(id =>
        !genre.categories.some(category => `c${category.inner_id}` === id)
    )
  }
}

async function updatePassword() {
  if (!isPasswordValid.value) return

  passwordUpdating.value = true
  try {
    await axios.put(`/api/users/${props.userId}`, {
      password: newPassword.value
    })
    newPassword.value = ''
    confirmNewPassword.value = ''
    emit('user-updated')
  } catch (error) {
    console.error('Error updating password:', error)
  } finally {
    passwordUpdating.value = false
  }
}

async function updatePermissions() {
  permissionsUpdating.value = true
  try {
    const allowedGenres = selectedPermissions.value
        .filter(id => id.startsWith('g'))
        .map(id => parseInt(id.slice(1)))
    const allowedCategories = selectedPermissions.value
        .filter(id => id.startsWith('c'))
        .map(id => parseInt(id.slice(1)))

    await axios.put(`/api/users/${props.userId}`, {
      allowed_genres: allowedGenres,
      allowed_categories: allowedCategories
    })
    originalPermissions.value = [...selectedPermissions.value]
    emit('user-updated')
  } catch (error) {
    console.error('Error updating permissions:', error)
  } finally {
    permissionsUpdating.value = false
  }
}
</script>

<style scoped>
.permissions-tree {
  max-height: 400px;
  overflow-y: auto;
}

.genre-item {
  margin-bottom: 10px;
}

.category-item {
  margin-left: 20px;
}
</style>